'use client';

import Link from 'next/link';
import { useParams } from 'next/navigation';
import { FormattedMessage } from 'react-intl';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Box } from '@mui/material';
import { Button, Stack, Typography } from '@/primitives';
import { PUBLIC_ROUTES } from '@/configs/routes';
import { blue, slate } from '@/lib/colours';
import { BackgroundVideo } from '../BackgroundVideo';

/**
 * Rendered by `[orgName]/layout.tsx` when the URL's societyCode cannot be
 * resolved to any society. Uses the same video backdrop as `/search` so the
 * 404 feels continuous with the org-picker experience (the layout also flags
 * `notFound` on the public search store so `useIsPublicSearch` falls back to
 * the generic non-society site header).
 */
export default function SocietyNotFoundPage() {
  const { orgName } = useParams<{ orgName?: string }>();
  const societyCode = orgName?.trim() ?? '';

  return (
    <Box
      component="section"
      aria-labelledby="society-not-found-title"
      sx={{
        position: 'relative',
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
      }}
    >
      <BackgroundVideo />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, md: 4 },
          py: { xs: 6, md: 10 },
        }}
      >
        <Stack
          alignItems="center"
          spacing={3}
          sx={{
            width: '100%',
            maxWidth: 520,
            textAlign: 'center',
            p: { xs: 3, md: 5 },
            borderRadius: '16px',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.25)',
            backdropFilter: 'saturate(1.1)',
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: 96,
              height: 96,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${blue[50]} 0%, ${blue[100]} 100%)`,
              color: blue[600],
            }}
          >
            <TravelExploreIcon sx={{ fontSize: 52 }} />
          </Box>

          <Stack spacing={1.5} alignItems="center">
            <Typography
              variant="labelsmall"
              sx={{
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: blue[600],
                fontWeight: 600,
              }}
            >
              <FormattedMessage
                id="societyNotFound.eyebrow"
                defaultMessage="404 · Society not found"
              />
            </Typography>

            <Typography
              id="society-not-found-title"
              variant="headlinemedium"
              component="h1"
              sx={{ color: 'text.primary', letterSpacing: '-0.01em' }}
            >
              <FormattedMessage
                id="societyNotFound.title"
                defaultMessage="We couldn't find that society"
              />
            </Typography>

            <Typography
              variant="bodymedium"
              component="p"
              sx={{ color: 'text.secondary', lineHeight: 1.6 }}
            >
              {societyCode ? (
                <FormattedMessage
                  id="societyNotFound.bodyWithCode"
                  defaultMessage="No society matches the code {code}. Check the URL for typos, or head back to search and pick one from the list."
                  values={{
                    code: (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          px: 1,
                          py: 0.25,
                          mx: 0.25,
                          borderRadius: '6px',
                          bgcolor: slate[100],
                          color: slate[800],
                          fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, monospace',
                          fontSize: '0.9em',
                          fontWeight: 600,
                          border: `1px solid ${slate[200]}`,
                        }}
                      >
                        {societyCode}
                      </Box>
                    ),
                  }}
                />
              ) : (
                <FormattedMessage
                  id="societyNotFound.bodyNoCode"
                  defaultMessage="No society matches this URL. Head back to search and pick one from the list."
                />
              )}
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ width: '100%', justifyContent: 'center', pt: 1 }}
          >
            <Button
              component={Link}
              href={PUBLIC_ROUTES.SEARCH}
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardRoundedIcon />}
              sx={{ textTransform: 'none', px: 2.5, py: 1 }}
            >
              <FormattedMessage
                id="societyNotFound.ctaSearch"
                defaultMessage="Back to database search"
              />
            </Button>
            <Button
              component={Link}
              href={PUBLIC_ROUTES.HOME}
              variant="outlined"
              color="primary"
              sx={{ textTransform: 'none', px: 2.5, py: 1 }}
            >
              <FormattedMessage
                id="societyNotFound.ctaHome"
                defaultMessage="Go to home"
              />
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
